import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useReduxState } from "hooks/useReduxState";
import {
  setNikkeiOffset,
  setNikkeiLimit,
  setNikkeiListLimit,
  setNikkeiSortValue,
  setNikkeiKeyword,
  setNikkeiSearchedKeyword,
  setIsSearch
} from "modules/binders/list/actions";
import { setNikkeiCardViewMode } from "modules/binders/saved/actions";
import { useToast } from "hooks/useToast";
import { LAZY_LOAD_LIMIT } from "features/BinderPage/List";
import { trackAction } from "utils/helper/analytics";
import { useMergeCollection } from "../../useMergeCollection";
import { useRequestMutation } from "hooks/useRequest";
import { BinderItem } from "models/BinderItem";

type ErrorType = {
  message: string;
  body: {
    code: number;
    details: {
      apiErrorCode: string;
      message: string;
      stackTrace?: string;
      errorType?: string;
    }[];
  };
};

const KEYWORD_MAX_LENGTH = 500;

export const useNikkeiBinder = () => {
  const [isError, setIsError] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();
  const cardViewMode = useReduxState(s => s.binders.saved.nikkeiCardViewMode);
  const offset = useReduxState(s => s.binders.list.nikkeiOffset);
  const limit = useReduxState(s => s.binders.list.nikkeiLimit);
  const listLimit = useReduxState(s => s.binders.list.nikkeiListLimit);
  const sortValue = useReduxState(s => s.binders.list.nikkeiSortValue);
  const nikkeiKeyword = useReduxState(s => s.binders.list.nikkeiKeyword) ?? "";
  // 並び替えなどの操作時は、入力中(未確定)の値ではなく、
  // 直前にAPIへリクエストしたキーワード(または空)でリクエストする
  // タブ切替で再マウントされても失われないよう、reduxに保持する
  const nikkeiSearchedKeyword =
    useReduxState(s => s.binders.list.nikkeiSearchedKeyword) ?? "";
  const isSearch = useReduxState(s => s.binders.list.isSearch);

  const { trigger: fetchNikkeiCollectionTrigger } = useRequestMutation<{
    binderList: BinderItem[];
    totalCount: number;
  }>("/binder/nikkei", "GET");
  const {
    collections,
    isLoading,
    isAdding,
    totalCount,
    fetchCollections,
    addCollection
  } = useMergeCollection(fetchNikkeiCollectionTrigger, "nikkei");

  const fetchErrorHandle = useCallback(
    (error: ErrorType) => {
      const errors = [];
      if (error && error.body && error.body.details) {
        for (const detail of error.body.details) {
          errors.push(detail.message);
        }
      }
      toast.setErrors(
        error.message === "GATEWAY_TIMEOUT"
          ? "コレクションの読み込みがタイムアウトしました"
          : errors
      );
      setIsError(error.message === "GATEWAY_TIMEOUT");
    },
    [toast]
  );

  const fetchNikkeiBinders = useCallback(async () => {
    const setLimit = cardViewMode ? LAZY_LOAD_LIMIT.card : limit;
    try {
      dispatch(setNikkeiOffset(0));
      dispatch(setNikkeiLimit(setLimit));
      dispatch(setIsSearch(!!nikkeiSearchedKeyword));
      dispatch(
        setNikkeiSortValue(
          !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue
        )
      );

      fetchCollections({
        needFeedArticles: location.pathname === "/" || cardViewMode ? 1 : 0,
        offset: 0,
        limit: setLimit,
        sortKbn: !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue,
        keyword: nikkeiSearchedKeyword
      });
    } catch (error) {
      fetchErrorHandle(error as ErrorType);
    }
  }, [
    cardViewMode,
    limit,
    dispatch,
    nikkeiSearchedKeyword,
    sortValue,
    fetchCollections,
    fetchErrorHandle
  ]);

  const addNikkeiBinders = useCallback(async () => {
    if (offset + limit < totalCount) {
      dispatch(setNikkeiOffset(offset + limit));
      addCollection({
        needFeedArticles: cardViewMode ? 1 : 0,
        limit,
        offset: offset + limit,
        sortKbn: sortValue,
        keyword: nikkeiSearchedKeyword
      });
    }
  }, [
    addCollection,
    cardViewMode,
    dispatch,
    limit,
    nikkeiSearchedKeyword,
    offset,
    sortValue,
    totalCount
  ]);

  const sortNikkeiBinders = async (sortKbn: string) => {
    try {
      dispatch(setNikkeiOffset(0));
      dispatch(setIsSearch(!!nikkeiSearchedKeyword));
      dispatch(
        setNikkeiSortValue(
          !nikkeiSearchedKeyword && sortKbn === "3" ? "1" : sortKbn
        )
      );
      fetchCollections({
        needFeedArticles: cardViewMode ? 1 : 0,
        sortKbn,
        offset: 0,
        limit,
        keyword: nikkeiSearchedKeyword
      });
    } catch (error) {
      fetchErrorHandle(error as ErrorType);
    }
  };

  const setCardViewMode = (isCardMode: boolean) => {
    dispatch(setNikkeiCardViewMode(isCardMode));
  };

  const switchCardViewMode = useCallback(
    async (cardMode: boolean) => {
      try {
        const changedLimit = cardMode ? LAZY_LOAD_LIMIT.card : listLimit;
        if (!cardMode) {
          dispatch(setNikkeiListLimit(changedLimit));
        }
        dispatch(setNikkeiLimit(changedLimit));
        dispatch(setNikkeiCardViewMode(cardMode));
        dispatch(setNikkeiOffset(0));
        dispatch(setIsSearch(!!nikkeiSearchedKeyword));
        dispatch(
          setNikkeiSortValue(
            !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardMode ? 1 : 0,
          sortKbn:
            !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue,
          keyword: nikkeiSearchedKeyword,
          limit: changedLimit
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      listLimit,
      nikkeiSearchedKeyword,
      sortValue,
      fetchCollections,
      fetchErrorHandle
    ]
  );

  const changePage = useCallback(
    async (offset: number, limit: number) => {
      try {
        dispatch(setNikkeiOffset(offset));
        dispatch(setNikkeiLimit(limit));
        dispatch(setIsSearch(!!nikkeiSearchedKeyword));
        dispatch(
          setNikkeiSortValue(
            !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset,
          limit,
          keyword: nikkeiSearchedKeyword,
          sortKbn: !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      nikkeiSearchedKeyword,
      sortValue,
      fetchCollections,
      cardViewMode,
      fetchErrorHandle
    ]
  );

  const changeLimit = useCallback(
    async (limit: number) => {
      try {
        dispatch(setNikkeiLimit(limit));
        dispatch(setNikkeiListLimit(limit));
        dispatch(setNikkeiOffset(0));
        dispatch(setIsSearch(!!nikkeiSearchedKeyword));
        dispatch(
          setNikkeiSortValue(
            !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue
          )
        );

        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset: 0,
          limit,
          keyword: nikkeiSearchedKeyword,
          sortKbn: !nikkeiSearchedKeyword && sortValue === "3" ? "1" : sortValue
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [
      dispatch,
      nikkeiSearchedKeyword,
      sortValue,
      fetchCollections,
      cardViewMode,
      fetchErrorHandle
    ]
  );

  const onKeywordSearch = useCallback(
    async (keyword: string) => {
      // 500文字を超える場合はAPIを実行しない
      if (keyword.length > KEYWORD_MAX_LENGTH) return;
      try {
        trackAction("myCollectionSearchKeyword", keyword);
        dispatch(setNikkeiKeyword(keyword));
        dispatch(setNikkeiSearchedKeyword(keyword));
        dispatch(setIsSearch(!!keyword));
        dispatch(setNikkeiSortValue(keyword ? "3" : "1"));
        dispatch(setNikkeiOffset(0));
        fetchCollections({
          needFeedArticles: cardViewMode ? 1 : 0,
          offset: 0,
          limit,
          sortKbn: keyword ? "3" : "1",
          keyword
        });
      } catch (error) {
        fetchErrorHandle(error as ErrorType);
      }
    },
    [dispatch, fetchCollections, cardViewMode, limit, fetchErrorHandle]
  );

  return {
    isLoading,
    sortValue,
    binderItems: collections,
    isError,
    fetchNikkeiBinders,
    sortNikkeiBinders,
    cardViewMode,
    totalCount,
    setCardViewMode,
    addNikkeiBinders,
    limit,
    offset,
    isAdding,
    switchCardViewMode,
    changePage,
    changeLimit,
    onKeywordSearch,
    nikkeiKeyword,
    setNikkeiKeyword,
    isSearch
  };
};
